import React, { memo } from 'react';
import PropTypes from 'prop-types';
import { ToggleButton } from '@mui/material';
import AppToolTip from '../InputBox/AppToolTip';
import { colors, borders } from '../../Tokens/Colors';
import { borderRadius, fontSizes, fontWeights, spacing } from '../../Tokens/Scales';
import { sizes } from '../../Tokens/Sizes';

// Compact heights so a row of toggles sits inline with labels and small inputs
// (property panels, table toolbars) instead of at full AppButton height.
const HEIGHTS = { small: 24, medium: 28 };

const getToggleStyles = (size) => ({
  height: HEIGHTS[size] || HEIGHTS.small,
  minWidth: HEIGHTS[size] || HEIGHTS.small,
  px: `${size === 'medium' ? spacing.sm : spacing.xs + 2}px`,
  gap: `${spacing.xs}px`,
  textTransform: 'none',
  fontSize: size === 'medium' ? fontSizes.sm : fontSizes.xs,
  fontWeight: fontWeights.medium,
  lineHeight: 1,
  color: colors.text.secondary,
  backgroundColor: colors.background.surface,
  border: borders.default,
  borderRadius: `${borderRadius.sm}px`,
  transition: 'background-color 0.15s ease, color 0.15s ease, border-color 0.15s ease',
  '& .MuiSvgIcon-root': { fontSize: sizes.icon.xsmall },
  '&:hover': {
    backgroundColor: colors.border.focusTint08,
    color: colors.text.primary,
  },
  '&.Mui-selected, &.Mui-selected:hover': {
    backgroundColor: colors.border.focusTint12,
    borderColor: colors.border.focus,
    color: colors.border.focus,
    fontWeight: fontWeights.semibold,
  },
  '&.Mui-disabled': {
    color: colors.text.disabled,
    backgroundColor: colors.background.disabled,
  },
});

const TOGGLE_STYLES = {
  small: getToggleStyles('small'),
  medium: getToggleStyles('medium'),
};

/**
 * AppToggleButton — a single on/off button that stays pressed while active.
 * Each button is independent, so several can sit side by side and be on at once.
 *
 * Use for: Bold / Italic formatting, border sides (Top / Right / Bottom / Left),
 * a compact on/off flag inline with other controls (Accounting, Merge).
 * Not for: picking one of several peer options (AppViewSwitcher), a labelled
 * form setting (AppSwitch / AppCheckbox), or a one-shot action (AppButton).
 *
 * @param {boolean}  selected   Whether the button is pressed
 * @param {function} onChange   Called with the next selected state and the event
 * @param {node}     label      Button text (or pass children)
 * @param {elementType|element} icon  Optional leading icon
 * @param {node}     tooltip    Optional hover text
 * @param {string}   value      Value passed to MUI; defaults to the label when it is a string
 * @param {string}   size       'small' (24px) | 'medium' (28px)
 */
const AppToggleButton = ({
  selected = false,
  onChange,
  label,
  children,
  icon: Icon,
  tooltip = '',
  value,
  disabled = false,
  size = 'small',
  ariaLabel,
  sx,
  ...props
}) => {
  const iconNode = React.isValidElement(Icon) ? Icon : Icon ? <Icon /> : null;
  const content = label ?? children;
  const baseStyles = TOGGLE_STYLES[size] || TOGGLE_STYLES.small;

  const button = (
    <ToggleButton
      value={value ?? (typeof content === 'string' ? content : 'toggle')}
      selected={Boolean(selected)}
      disabled={disabled}
      onChange={(event) => onChange?.(!selected, event)}
      aria-label={ariaLabel || (typeof content === 'string' ? content : undefined)}
      sx={sx ? [baseStyles, sx] : baseStyles}
      {...props}
    >
      {iconNode}
      {content}
    </ToggleButton>
  );

  if (!tooltip) return button;

  // The span keeps the tooltip working while the button is disabled.
  return (
    <AppToolTip title={tooltip}>
      <span style={{ display: 'inline-flex' }}>{button}</span>
    </AppToolTip>
  );
};

AppToggleButton.propTypes = {
  selected: PropTypes.bool,
  onChange: PropTypes.func,
  label: PropTypes.node,
  children: PropTypes.node,
  icon: PropTypes.oneOfType([PropTypes.element, PropTypes.elementType]),
  tooltip: PropTypes.node,
  value: PropTypes.string,
  disabled: PropTypes.bool,
  size: PropTypes.oneOf(['small', 'medium']),
  ariaLabel: PropTypes.string,
  sx: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
};

export default memo(AppToggleButton);
